import { useCallback, useEffect, useState } from "react"

export interface UseMaintenanceModeProps {
    maintenanceFilePath?: string
}

/**
 * React hook that loads a maintenance config file from a specified location and returns a list of services currently
 * in maintenance, allowing the UI to update accordingly.
 *
 * | Env Variable                                    | Description                                                                                 |
 * | ----------------------------------------------- | ------------------------------------------------------------------------------------------- |
 * | <code>REACT_APP_MAINTENANCE_ROUTE</code>        | Alternative way to specify the `maintenanceFilePath` argument                               |
 *
 * @param {UseMaintenanceModeProps} props Arguments to the react hook
 * @param {string} [props.maintenanceFilePath] The root url to load the maintenance.json file from
 * @returns {Array<string> | undefined} List of services currently in maintenance
 *
 * @example
 *
 * ``` typescript
 * const servicesInMaintenance = useMaintenanceMode()
 *
 * if (servicesInMaintenance && servicesInMaintenance.includes("gms"))
 *  window.location.pathname = "/maintenance"
 * ```
 */
export default function useMaintenanceMode({
    maintenanceFilePath,
}: UseMaintenanceModeProps) {
    if (!maintenanceFilePath)
        maintenanceFilePath = process.env.REACT_APP_MAINTENANCE_ROUTE ?? "/"

    if (!maintenanceFilePath.endsWith("/")) maintenanceFilePath += "/"

    const [servicesInMaintenance, setServicesInMaintenance] = useState<
        string[] | undefined
    >()

    const checkMaintenance = useCallback(async () => {
        try {
            const res = await fetch(`${maintenanceFilePath}maintenance.json`)
            const services: Record<string, boolean> = await res.json()
            setServicesInMaintenance(
                Object.entries(services)
                    .filter(([, v]) => v)
                    .map(([k]) => k)
            )
        } catch (e) {
            setServicesInMaintenance(undefined)
        }
    }, [setServicesInMaintenance])

    useEffect(() => {
        checkMaintenance()
    }, [checkMaintenance])

    return servicesInMaintenance
}
